import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Button } from '../components/ui/Button';
import { ErrorBanner } from '../components/ui/ErrorBanner';
import { PageHeader } from '../components/ui/PageHeader';
import { Select } from '../components/ui/Select';
import { api } from '../lib/api';
import { AUFTRAG_STATUS_BADGE, AUFTRAG_STATUS_LABEL, type Auftrag } from '../lib/auftraege-api';
import {
  MANGEL_KATEGORIE_LABEL,
  SLA_BANNER_STYLE,
  computeSlaInfo,
  reklamationenApi,
  type MangelKategorie,
} from '../lib/reklamationen-api';
import { requireAccess } from '../lib/guards';
import { formatGermanDateFromTimestamp } from '../lib/date-format';

export const Route = createFileRoute('/reklamationen/$id')({
  beforeLoad: () => requireAccess('/api/v1/reklamationen'),
  component: ReklamationDetailPage,
});

interface SubunternehmerListItem {
  id: string;
  firmenname: string;
  ansprechpartner: string | null;
}

function ReklamationDetailPage(): JSX.Element {
  const { id } = Route.useParams();
  return <ReklamationDetail id={id} />;
}

export function ReklamationDetail({ id }: { id: string }): JSX.Element {
  const navigate = useNavigate();
  const qc = useQueryClient();

  const reklamationQ = useQuery({
    queryKey: ['reklamationen', 'detail', id],
    queryFn: () => reklamationenApi.getById(id),
  });

  const invalidate = (): void => {
    void qc.invalidateQueries({ queryKey: ['reklamationen', 'detail', id] });
    void qc.invalidateQueries({ queryKey: ['reklamationen', 'list'] });
  };

  const zuordnenSub = useMutation({
    mutationFn: (subunternehmerId: string) => reklamationenApi.zuordnenSub(id, subunternehmerId),
    onSuccess: invalidate,
  });

  const starten = useMutation({
    mutationFn: () => reklamationenApi.starten(id),
    onSuccess: invalidate,
  });

  const abschliessen = useMutation({
    mutationFn: (notiz?: string) => reklamationenApi.abschliessen(id, notiz),
    onSuccess: invalidate,
  });

  const reklamation = reklamationQ.data?.item;
  const sla = reklamation ? computeSlaInfo(reklamation.gewaehrleistungBis) : null;
  const isOpen =
    reklamation?.status === 'entwurf' ||
    reklamation?.status === 'bestaetigt' ||
    reklamation?.status === 'in_arbeit';

  return (
    <div className="space-y-6">
      <PageHeader
        title={reklamation?.titel ?? 'Reklamation'}
        description={
          reklamation
            ? `${
                reklamation.mangelKategorie
                  ? MANGEL_KATEGORIE_LABEL[reklamation.mangelKategorie as MangelKategorie]
                  : 'Kategorie ?'
              } · gemeldet ${formatGermanDateFromTimestamp(reklamation.createdAt)}`
            : '…'
        }
        actions={
          <Button variant="ghost" onClick={() => void navigate({ to: '/reklamationen' })}>
            ← Zurück
          </Button>
        }
      />

      {reklamationQ.error ? <ErrorBanner error={reklamationQ.error as Error} /> : null}
      {zuordnenSub.error ? <ErrorBanner error={zuordnenSub.error as Error} /> : null}
      {starten.error ? <ErrorBanner error={starten.error as Error} /> : null}
      {abschliessen.error ? <ErrorBanner error={abschliessen.error as Error} /> : null}

      {!reklamation ? (
        <div className="text-sm text-slate-500">Lädt…</div>
      ) : (
        <>
          {sla && sla.level !== 'keiner' && isOpen ? (
            <div
              className={`rounded-md border p-3 text-sm font-medium ${SLA_BANNER_STYLE[sla.level]}`}
            >
              {sla.daysLeft <= 0
                ? `Gewährleistungsfrist abgelaufen (${reklamation.gewaehrleistungBis})`
                : `Gewährleistung läuft in ${sla.daysLeft} Tag${
                    sla.daysLeft === 1 ? '' : 'en'
                  } ab (${reklamation.gewaehrleistungBis})`}
            </div>
          ) : null}

          <div className="flex flex-wrap items-center gap-3">
            <span
              className={`rounded-md border px-2 py-1 text-xs font-medium ${AUFTRAG_STATUS_BADGE[reklamation.status]}`}
            >
              {AUFTRAG_STATUS_LABEL[reklamation.status]}
            </span>
          </div>

          <StatusAktionen
            reklamation={reklamation}
            onStarten={() => starten.mutate()}
            onAbschliessen={(notiz) => abschliessen.mutate(notiz)}
            startenPending={starten.isPending}
            abschliessenPending={abschliessen.isPending}
          />

          <SubZuordnenPanel
            reklamation={reklamation}
            onZuordnen={(subId) => zuordnenSub.mutate(subId)}
            pending={zuordnenSub.isPending}
          />

          <StammDaten reklamation={reklamation} />
        </>
      )}
    </div>
  );
}

/**
 * State-Machine-Subset (Backend `reklamationen-service`):
 *   entwurf  -> bestaetigt   (auto beim ersten zuordnenSub)
 *   bestaetigt -> in_arbeit  (starten)
 *   bestaetigt -> abgeschlossen (abschliessen, ohne Start)
 *   in_arbeit -> abgeschlossen  (abschliessen)
 * `entwurf` ohne Sub: einzige sinnvolle Aktion ist „Sub zuordnen" — der
 * Service springt dann auf `bestaetigt`. Aktionen werden nur gerendert,
 * wenn der Status sie zulässt.
 */
function StatusAktionen({
  reklamation,
  onStarten,
  onAbschliessen,
  startenPending,
  abschliessenPending,
}: {
  reklamation: Auftrag;
  onStarten: () => void;
  onAbschliessen: (notiz?: string) => void;
  startenPending: boolean;
  abschliessenPending: boolean;
}): JSX.Element | null {
  const [showAbschliessen, setShowAbschliessen] = useState(false);
  const [notiz, setNotiz] = useState('');
  const pending = startenPending || abschliessenPending;

  const canStart = reklamation.status === 'bestaetigt';
  const canFinish = reklamation.status === 'bestaetigt' || reklamation.status === 'in_arbeit';

  if (!canStart && !canFinish) return null;

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-sm font-medium text-slate-700">Status-Aktionen</div>
      <div className="flex flex-wrap gap-2">
        {canStart ? (
          <Button onClick={onStarten} disabled={pending}>
            {startenPending ? 'Starte…' : 'Bearbeitung starten'}
          </Button>
        ) : null}
        {canFinish ? (
          <Button variant="ghost" onClick={() => setShowAbschliessen((v) => !v)} disabled={pending}>
            {showAbschliessen ? 'Abbrechen' : 'Abschließen…'}
          </Button>
        ) : null}
      </div>
      {showAbschliessen && canFinish ? (
        <div className="space-y-2 border-t border-slate-200 pt-3">
          <label className="block">
            <span className="mb-1 block text-sm font-medium text-slate-700">
              Abschluss-Notiz (optional)
            </span>
            <textarea
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500"
              rows={3}
              value={notiz}
              onChange={(e) => setNotiz(e.target.value)}
              placeholder="Was wurde behoben? Wer hat geprüft?"
            />
          </label>
          <Button
            onClick={() => {
              const trimmed = notiz.trim();
              onAbschliessen(trimmed.length > 0 ? trimmed : undefined);
              setShowAbschliessen(false);
              setNotiz('');
            }}
            disabled={pending}
          >
            {abschliessenPending ? 'Schließe ab…' : 'Reklamation abschließen'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

/**
 * Sub-Zuordnung ist auch nach erstmaliger Zuordnung möglich (PATCH-Route
 * akzeptiert Wechsel). Nach Abschluss wird das Panel zur reinen Anzeige
 * — kein neuer Sub mehr setzbar.
 */
function SubZuordnenPanel({
  reklamation,
  onZuordnen,
  pending,
}: {
  reklamation: Auftrag;
  onZuordnen: (subId: string) => void;
  pending: boolean;
}): JSX.Element {
  const [selectedSubId, setSelectedSubId] = useState('');

  const subsQ = useQuery({
    queryKey: ['subunternehmer', 'list'],
    queryFn: () => api<{ items: SubunternehmerListItem[] }>('/api/v1/subunternehmer'),
  });

  const subs = subsQ.data?.items ?? [];
  const aktuellerSub = subs.find((s) => s.id === reklamation.subunternehmerId);
  const isClosed = reklamation.status === 'abgeschlossen';
  const canSubmit = selectedSubId.length > 0 && selectedSubId !== reklamation.subunternehmerId;

  return (
    <div className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <div className="text-sm font-medium text-slate-700">Subunternehmer</div>
      <div className="text-sm text-slate-900">
        {aktuellerSub ? (
          <>
            <span className="font-medium">{aktuellerSub.firmenname}</span>
            {aktuellerSub.ansprechpartner ? (
              <span className="text-slate-500"> · {aktuellerSub.ansprechpartner}</span>
            ) : null}
          </>
        ) : (
          <span className="text-slate-500">Noch nicht zugeordnet</span>
        )}
      </div>

      {subsQ.error ? <ErrorBanner error={subsQ.error as Error} /> : null}

      {!isClosed ? (
        <div className="flex flex-wrap items-end gap-2 border-t border-slate-200 pt-3">
          <label className="min-w-[240px] flex-1">
            <span className="mb-1 block text-xs font-medium text-slate-600">
              {aktuellerSub ? 'Sub wechseln' : 'Sub zuordnen'}
            </span>
            <Select value={selectedSubId} onChange={(e) => setSelectedSubId(e.target.value)}>
              <option value="">– Subunternehmer wählen –</option>
              {subs.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.firmenname}
                  {s.ansprechpartner ? ` · ${s.ansprechpartner}` : ''}
                </option>
              ))}
            </Select>
          </label>
          <Button
            onClick={() => {
              if (canSubmit) onZuordnen(selectedSubId);
            }}
            disabled={!canSubmit || pending}
          >
            {pending ? 'Speichere…' : aktuellerSub ? 'Wechseln' : 'Zuordnen'}
          </Button>
        </div>
      ) : null}
    </div>
  );
}

function StammDaten({ reklamation }: { reklamation: Auftrag }): JSX.Element {
  return (
    <dl className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 sm:grid-cols-2">
      <Field label="Mangel-Kategorie">
        {reklamation.mangelKategorie ? (
          MANGEL_KATEGORIE_LABEL[reklamation.mangelKategorie as MangelKategorie]
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </Field>
      <Field label="Gewährleistung bis">
        {reklamation.gewaehrleistungBis ? (
          formatGermanDateFromTimestamp(reklamation.gewaehrleistungBis)
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </Field>
      <Field label="Kunde">
        <Link
          to="/kunden/$id"
          params={{ id: reklamation.kundeId }}
          className="text-brand-700 hover:underline"
        >
          Kunde öffnen
        </Link>
      </Field>
      <Field label="Objekt">
        {reklamation.objektId ? (
          <Link
            to="/objekte/$id"
            params={{ id: reklamation.objektId }}
            className="text-brand-700 hover:underline"
          >
            Objekt öffnen
          </Link>
        ) : (
          <span className="text-slate-400">—</span>
        )}
      </Field>
      <Field label="Beschreibung" wide>
        {reklamation.beschreibung ?? <span className="text-slate-400">—</span>}
      </Field>
    </dl>
  );
}

function Field({
  label,
  children,
  wide,
}: {
  label: string;
  children: React.ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <div className={wide ? 'sm:col-span-2' : ''}>
      <dt className="text-xs font-medium uppercase tracking-wide text-slate-500">{label}</dt>
      <dd className="mt-1 text-sm text-slate-900 whitespace-pre-wrap">{children}</dd>
    </div>
  );
}
